import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl, FormattedMessage } from '../../../../util/reactIntl';

import SectionContainer from '../SectionContainer';
import ListSpaceCta from '../../../SpaceOwnerLandingPage/ListSpaceCta';
import css from './SectionEarningsCalculator.module.css';

// Transparent, conservative assumptions used for the non-binding estimate.
// These are clearly disclosed to the user via the disclaimer copy.
const WORKING_DAYS_PER_MONTH = 22;
const BOOKABLE_HOURS_PER_DAY = 8;
const BOOKABLE_HOURS_PER_MONTH = BOOKABLE_HOURS_PER_DAY * WORKING_DAYS_PER_MONTH;

// Per space type: whether it is priced per day or per hour, and sensible
// starting values the owner can adjust to match their own space.
const TYPE_CONFIG = {
  desk: { unit: 'day', defaultPrice: 25, defaultUnits: 5, defaultOccupancy: 60 },
  dayOffice: { unit: 'day', defaultPrice: 70, defaultUnits: 1, defaultOccupancy: 50 },
  meeting: { unit: 'hour', defaultPrice: 35, defaultUnits: 1, defaultOccupancy: 25 },
};

const SPACE_TYPES = [
  { id: 'desk', labelId: 'SpaceOwnerLandingPage.calcTypeDesk' },
  { id: 'dayOffice', labelId: 'SpaceOwnerLandingPage.calcTypeDayOffice' },
  { id: 'meeting', labelId: 'SpaceOwnerLandingPage.calcTypeMeeting' },
];

const clamp = (val, min, max) => {
  const n = Number.isFinite(val) ? val : min;
  return Math.min(Math.max(n, min), max);
};

/**
 * Interactive earnings calculator for the Space Owner Landing Page.
 * Produces a non-binding monthly income estimate from the owner's own inputs
 * (space type, number of units, price, estimated occupancy). No fabricated data.
 *
 * @component
 * @param {Object} props
 * @param {string} props.sectionId
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {Object?} props.defaultClasses
 * @param {Object?} props.appearance
 * @param {Object?} props.options
 * @returns {JSX.Element}
 */
const SectionEarningsCalculator = props => {
  const { sectionId, className, rootClassName, defaultClasses, appearance, options } = props;
  const intl = useIntl();

  const [type, setType] = useState('desk');
  const [units, setUnits] = useState(TYPE_CONFIG.desk.defaultUnits);
  const [price, setPrice] = useState(TYPE_CONFIG.desk.defaultPrice);
  const [occupancy, setOccupancy] = useState(TYPE_CONFIG.desk.defaultOccupancy);

  const onTypeChange = nextType => {
    const cfg = TYPE_CONFIG[nextType];
    setType(nextType);
    setUnits(cfg.defaultUnits);
    setPrice(cfg.defaultPrice);
    setOccupancy(cfg.defaultOccupancy);
  };

  const cfg = TYPE_CONFIG[type];
  const periodsPerMonth = cfg.unit === 'hour' ? BOOKABLE_HOURS_PER_MONTH : WORKING_DAYS_PER_MONTH;

  const safePrice = clamp(Number(price), 0, 100000);
  const safeUnits = clamp(Number(units), 1, 999);
  const safeOccupancy = clamp(Number(occupancy), 0, 100);

  const rawMonthly = safePrice * safeUnits * periodsPerMonth * (safeOccupancy / 100);
  const monthly = Math.round(rawMonthly / 10) * 10;

  const formattedMonthly = new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    maximumFractionDigits: 0,
  })
    .format(monthly)
    // Normalise the Swiss thousands separator to a plain apostrophe so the server (Node
    // ICU) and the client (browser ICU) render identical text. Newer ICU emits U+2019 (’)
    // while others emit U+0027 ('); the difference is a React SSR hydration mismatch
    // (#418/#425). Applying the same replace on both sides makes the output deterministic.
    .replace(/’/g, "'");

  const priceUnitLabel = intl.formatMessage({
    id:
      cfg.unit === 'hour'
        ? 'SpaceOwnerLandingPage.calcPriceUnitHour'
        : 'SpaceOwnerLandingPage.calcPriceUnitDay',
  });

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName, css.root)}
      appearance={appearance}
      options={options}
    >
      <div className={css.wrapper}>
        <h2 className={classNames(defaultClasses?.title, css.title)}>
          <FormattedMessage id="SpaceOwnerLandingPage.calcTitle" />
        </h2>
        <p className={classNames(defaultClasses?.description, css.subtitle)}>
          <FormattedMessage id="SpaceOwnerLandingPage.calcSubtitle" />
        </p>

        <div className={css.card}>
          <div className={css.controls}>
            <fieldset className={css.field}>
              <legend className={css.label}>
                <FormattedMessage id="SpaceOwnerLandingPage.calcSpaceTypeLabel" />
              </legend>
              <div className={css.segmented} role="group">
                {SPACE_TYPES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className={classNames(css.segment, { [css.segmentActive]: type === t.id })}
                    onClick={() => onTypeChange(t.id)}
                    aria-pressed={type === t.id}
                  >
                    <FormattedMessage id={t.labelId} />
                  </button>
                ))}
              </div>
            </fieldset>

            <div className={css.row}>
              <label className={css.field}>
                <span className={css.label}>
                  <FormattedMessage id="SpaceOwnerLandingPage.calcUnitsLabel" />
                </span>
                <input
                  type="number"
                  min="1"
                  max="999"
                  inputMode="numeric"
                  value={units}
                  onChange={e => setUnits(e.target.value)}
                  className={css.input}
                />
              </label>

              <label className={css.field}>
                <span className={css.label}>
                  <FormattedMessage
                    id="SpaceOwnerLandingPage.calcPriceLabel"
                    values={{ unit: priceUnitLabel }}
                  />
                </span>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  className={css.input}
                />
              </label>
            </div>

            <div className={css.field}>
              <span className={css.label}>
                <FormattedMessage
                  id="SpaceOwnerLandingPage.calcOccupancyLabel"
                  values={{ value: safeOccupancy }}
                />
              </span>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={safeOccupancy}
                onChange={e => setOccupancy(e.target.value)}
                className={css.range}
                aria-label={intl.formatMessage(
                  { id: 'SpaceOwnerLandingPage.calcOccupancyLabel' },
                  { value: safeOccupancy }
                )}
              />
            </div>
          </div>

          <div className={css.result} aria-live="polite">
            <span className={css.resultLabel}>
              <FormattedMessage id="SpaceOwnerLandingPage.calcResultLabel" />
            </span>
            <span className={css.resultValue}>{formattedMonthly}</span>
            <span className={css.resultSuffix}>
              <FormattedMessage id="SpaceOwnerLandingPage.calcResultSuffix" />
            </span>
            <ListSpaceCta ctaId="so-calc-list-cta" className={css.cta}>
              <FormattedMessage id="SpaceOwnerLandingPage.calcCta" />
            </ListSpaceCta>
          </div>
        </div>

        <p className={css.disclaimer}>
          <FormattedMessage id="SpaceOwnerLandingPage.calcDisclaimer" />
        </p>
      </div>
    </SectionContainer>
  );
};

export default SectionEarningsCalculator;
